/**
 * ProjectLifecycleStepper — shared progress visualizer used at the top of
 * both AdminProjectDetailPage and UserProjectDetailWithMandatoryUseCasesPage
 * so admin and user see the exact same lifecycle representation.
 *
 * States live in the backend enum (AppPreviewPreparation → Delivered). The
 * component accepts the normalized spaced form ("App preview preparation"
 * etc.) that the detail pages already produce via normalizeStatus().
 *
 * Terminal + non-sequential states:
 *   - "Finished testing" sits between Testing and Delivered in the state
 *     machine but isnt a separate circle; we mark Testing as completed
 *     and leave Delivered pending.
 *   - "Delivered" is terminal; every circle goes green.
 *   - "Rejected" breaks the sequence; we show a red notice below.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

interface LifecycleStep {
  key: string;
  i18nKey: string;
  fallback: string;
}

const LIFECYCLE_STEPS: LifecycleStep[] = [
  { key: 'App preview preparation', i18nKey: 'projectLifecycle.setup', fallback: 'Setup' },
  { key: 'App preview feedback', i18nKey: 'projectLifecycle.review', fallback: 'Review' },
  { key: 'Technical analysis', i18nKey: 'projectLifecycle.technicalAnalysis', fallback: 'Technical analysis' },
  { key: 'Sprint planning', i18nKey: 'projectLifecycle.sprintPlanning', fallback: 'Sprint planning' },
  { key: 'In development', i18nKey: 'projectLifecycle.development', fallback: 'Development' },
  { key: 'For testing', i18nKey: 'projectLifecycle.testing', fallback: 'Testing' },
  { key: 'Delivered', i18nKey: 'projectLifecycle.delivered', fallback: 'Delivered' },
];

export interface ProjectLifecycleStepperProps {
  /** Normalized project status (e.g. "App preview preparation"). */
  status: string;
  className?: string;
}

export function ProjectLifecycleStepper({ status, className = '' }: ProjectLifecycleStepperProps) {
  const { t } = useTranslation();

  let activeStepIndex = LIFECYCLE_STEPS.findIndex((s) => s.key === status);
  let completedThroughIndex = activeStepIndex - 1;
  if (activeStepIndex < 0 && status === 'Finished testing') {
    activeStepIndex = -1;
    completedThroughIndex = LIFECYCLE_STEPS.findIndex((s) => s.key === 'For testing');
  }
  if (status === 'Delivered') {
    activeStepIndex = -1;
    completedThroughIndex = LIFECYCLE_STEPS.length - 1;
  }

  const N = LIFECYCLE_STEPS.length;
  // Each circle sits at the center of its flex-1 cell. Cell width = 100/N %,
  // so the first circle center is at 100/(2N)% from the left edge and the
  // last circle center is symmetrically from the right.
  const edgeInsetPct = 100 / (2 * N);
  // Width covered by completed circles along the main track.
  const completedWidthPct = Math.max(0, completedThroughIndex) * (100 / N);

  return (
    <div
      className={`bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-5 ${className}`}
    >
      <div className="flex items-start justify-between relative">
        {/* Base connecting line (full track, muted) */}
        <div
          className="absolute top-4 h-0.5 bg-[var(--border-subtle)]"
          style={{ left: `${edgeInsetPct}%`, right: `${edgeInsetPct}%` }}
        />
        {/* Progress fill (green, covers completed steps) */}
        {completedThroughIndex >= 0 && (
          <div
            className="absolute top-4 h-0.5 bg-green-500 transition-all"
            style={{ left: `${edgeInsetPct}%`, width: `${completedWidthPct}%` }}
          />
        )}

        {LIFECYCLE_STEPS.map((step, idx) => {
          const isActive = idx === activeStepIndex;
          const isCompleted = idx <= completedThroughIndex;
          return (
            <div
              key={step.key}
              className="relative flex flex-col items-center z-10 flex-1 min-w-[64px]"
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-colors ${
                  isActive
                    ? 'bg-[var(--accent-primary)] border-[var(--accent-primary)] text-white'
                    : isCompleted
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'bg-[var(--surface-base)] border-[var(--border-default)] text-[var(--text-muted)]'
                }`}
              >
                {isCompleted ? (
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <span>{idx + 1}</span>
                )}
              </div>
              <div
                className={`mt-2 text-xs font-medium text-center ${
                  isActive
                    ? 'text-[var(--accent-primary)]'
                    : isCompleted
                      ? 'text-green-600'
                      : 'text-[var(--text-muted)]'
                }`}
              >
                {t(step.i18nKey, step.fallback)}
              </div>
            </div>
          );
        })}
      </div>

      {status === 'Rejected' && (
        <div className="mt-4 p-3 rounded-lg bg-red-600/10 border border-red-600/25 text-sm text-red-700 dark:text-red-500">
          {t(
            'projectLifecycle.rejectedNotice',
            'Project is Rejected — admin declined the initial review.',
          )}
        </div>
      )}
    </div>
  );
}

export default ProjectLifecycleStepper;
